import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // 检查本地存储的token
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      // 验证token并获取用户信息
      const response = await api.get('/auth/me');
      if (response.data.status === 'success') {
        setUser(response.data.data.user);
        setIsAuthenticated(true);
      } else {
        // token无效，清除本地存储
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
      }
    } catch (error) {
      console.error('检查认证状态失败:', error);
      // token可能过期或无效
      if (error.response?.status === 401) {
        await tryRefreshToken();
      } else {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
      }
    } finally {
      setLoading(false);
    }
  };

  const tryRefreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        return false;
      }

      const response = await api.post('/auth/refresh', {
        refreshToken
      });

      if (response.data.status === 'success') {
        const { accessToken, refreshToken: newRefreshToken, user } = response.data.data;
        
        localStorage.setItem('token', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        
        setUser(user);
        setIsAuthenticated(true);
        return true;
      }
    } catch (error) {
      console.error('刷新token失败:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
    }
    return false;
  };

  const login = async (credentials) => {
    try {
      setLoading(true);
      const response = await api.post('/auth/login', credentials);
      
      if (response.data.status === 'success') {
        const { accessToken, refreshToken, user } = response.data.data;
        
        // 存储tokens
        localStorage.setItem('token', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        
        // 更新状态
        setUser(user);
        setIsAuthenticated(true);
        
        return {
          success: true,
          user
        };
      } else {
        return {
          success: false,
          message: response.data.message || '登录失败'
        };
      }
    } catch (error) {
      console.error('登录失败:', error);
      return {
        success: false,
        message: error.response?.data?.message || '登录失败，请稍后重试'
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await api.post('/auth/logout', { refreshToken });
      }
    } catch (error) {
      console.error('退出登录失败:', error);
    } finally {
      // 无论API调用是否成功，都清除本地状态
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const register = async (userData) => {
    try {
      setLoading(true);
      const response = await api.post('/auth/register', userData);
      
      if (response.data.status === 'success') {
        return {
          success: true,
          message: response.data.message || '注册成功'
        };
      } else {
        return {
          success: false,
          message: response.data.message || '注册失败'
        };
      }
    } catch (error) {
      console.error('注册失败:', error);
      return {
        success: false,
        message: error.response?.data?.message || '注册失败，请稍后重试'
      };
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      setLoading(true);
      const response = await api.put('/auth/profile', profileData);
      
      if (response.data.status === 'success') {
        setUser(response.data.data.user);
        return {
          success: true,
          message: '个人信息更新成功'
        };
      } else {
        return {
          success: false,
          message: response.data.message || '更新失败'
        };
      }
    } catch (error) {
      console.error('更新个人信息失败:', error);
      return {
        success: false,
        message: error.response?.data?.message || '更新失败，请稍后重试'
      };
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (passwordData) => {
    try {
      setLoading(true);
      const response = await api.put('/auth/password', passwordData);
      
      if (response.data.status === 'success') {
        return {
          success: true,
          message: '密码修改成功'
        };
      } else {
        return {
          success: false,
          message: response.data.message || '密码修改失败'
        };
      }
    } catch (error) {
      console.error('修改密码失败:', error);
      return {
        success: false,
        message: error.response?.data?.message || '密码修改失败，请稍后重试'
      };
    } finally {
      setLoading(false);
    }
  };

  // 权限检查函数
  const hasPermission = (resource, action) => {
    if (!user || !isAuthenticated) {
      return false;
    }

    // 管理员拥有所有权限
    if (user.role === 'admin') {
      return true;
    }

    // 检查具体权限
    const permissions = user.permissions || {};
    const resourcePermissions = permissions[resource];
    
    if (!resourcePermissions) {
      return false;
    }

    return resourcePermissions[action] === true;
  };

  // 角色检查函数
  const hasRole = (roles) => {
    if (!user || !isAuthenticated) {
      return false;
    }

    const allowedRoles = Array.isArray(roles) ? roles : [roles];
    return allowedRoles.includes(user.role);
  };

  // 检查是否是管理员
  const isAdmin = () => {
    return user?.role === 'admin';
  };

  // 检查是否是编辑
  const isEditor = () => {
    return user?.role === 'editor';
  };

  // 获取用户显示名称
  const getDisplayName = () => {
    if (!user) return '未登录';
    return user.name || user.username || '用户';
  };

  // 获取用户角色显示名称
  const getRoleDisplayName = () => {
    if (!user) return '';
    switch (user.role) {
      case 'admin':
        return '管理员';
      case 'editor':
        return '编辑';
      default:
        return '用户';
    }
  };

  const value = {
    // 状态
    user,
    loading,
    isAuthenticated,
    
    // 认证方法
    login,
    logout,
    register,
    updateProfile,
    changePassword,
    checkAuthStatus,
    
    // 权限方法
    hasPermission,
    hasRole,
    isAdmin,
    isEditor,
    
    // 工具方法
    getDisplayName,
    getRoleDisplayName
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;